import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import firestoreService from '../../shared/services/firestoreService';
import { prepareImageForUpload } from '../../shared/imageResize';
import { MAX_DISH_PHOTOS, safeStorageImageUrl } from '../../shared/restaurantShowcase';
import { userMessage } from '../../shared/userFacingError';
import type { RestaurantPhoto } from '../../shared/types';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { CameraIcon, TrashIcon } from './icons/UiIcons';

/**
 * Photos de la vitrine (« Mon établissement ») : photo du lieu + galerie de plats.
 * Chaque action est enregistrée immédiatement (Storage + Firestore), sans passer
 * par le bouton « Enregistrer » du formulaire : un fichier envoyé mais jamais
 * enregistré resterait orphelin dans Storage.
 */

/** Formats proposés dans le sélecteur ; tout est ré-encodé en JPEG avant l'envoi. */
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp';

// ─────────────────────────────────────────────────────────────────────────────
// État des envois, conservé au niveau du module : si l'éditeur est démonté
// (navigation dans la sidebar) pendant un envoi, celui-ci continue ; au retour,
// l'éditeur sait qu'un envoi est en cours (pas de double envoi) et affiche
// l'erreur éventuelle. Tant qu'un envoi tourne, fermer l'onglet demande confirmation.
// ─────────────────────────────────────────────────────────────────────────────

type CoverBusy = 'upload' | 'remove' | null;
type DishBusy =
    | { kind: 'add'; done: number; total: number }
    | { kind: 'remove'; id: string }
    | { kind: 'reorder' }
    | null;

interface TaskState<B> {
    busy: B;
    error: string | null;
}

/** Une fonction par magasin : « un envoi est-il en cours ? » (garde de fermeture d'onglet). */
const busyChecks: Array<() => boolean> = [];

/** Aussi utilisé par les photos des plats de la carte (MenuItemPhotos.tsx). */
export function createTaskStore<B>(initial: TaskState<B>, isBusy: (busy: B) => boolean = (busy) => busy !== null) {
    let state = initial;
    const listeners = new Set<() => void>();
    busyChecks.push(() => isBusy(state.busy));
    return {
        get: () => state,
        set(patch: Partial<TaskState<B>>) {
            state = { ...state, ...patch };
            listeners.forEach((listener) => listener());
            syncUnloadGuard();
        },
        subscribe(listener: () => void) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
    };
}

const coverTask = createTaskStore<CoverBusy>({ busy: null, error: null });
const dishTask = createTaskStore<DishBusy>({ busy: null, error: null });

const preventUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
};
let unloadGuardActive = false;
function syncUnloadGuard() {
    const busy = busyChecks.some((check) => check());
    if (busy && !unloadGuardActive) window.addEventListener('beforeunload', preventUnload);
    if (!busy && unloadGuardActive) window.removeEventListener('beforeunload', preventUnload);
    unloadGuardActive = busy;
}

export function useTask<B>(store: ReturnType<typeof createTaskStore<B>>) {
    return useSyncExternalStore(store.subscribe, store.get);
}

/** true tant que le composant est monté (pas de focus ni d'annonce après démontage). */
export function useMountedRef() {
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    return mounted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Petits éléments communs
// ─────────────────────────────────────────────────────────────────────────────

export const ErrorBox: React.FC<{ message: string | null }> = ({ message }) =>
    message ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{message}</p>
    ) : null;

/** Région toujours présente : seul son texte change, pour les lecteurs d'écran. */
export const StatusRegion: React.FC<{ text: string }> = ({ text }) => (
    <p role="status" aria-live="polite" className="sr-only">{text}</p>
);

const ArrowIcon: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
);

// Boutons rendus inactifs via aria-disabled (et non disabled) : ils gardent le
// focus clavier pendant une opération ; le gestionnaire ignore alors le clic.
const actionButton = 'px-4 py-2 bg-peach-100 text-brand-700 font-bold rounded-lg hover:bg-peach-200 active:scale-95 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed';

// ─────────────────────────────────────────────────────────────────────────────
// Photo du lieu
// ─────────────────────────────────────────────────────────────────────────────

interface CoverPhotoEditorProps {
    restaurantId: number | string;
    imageUrl?: string;
    onChange: (cover: { imageUrl?: string; imageStoragePath?: string }) => void;
}

export const CoverPhotoEditor: React.FC<CoverPhotoEditorProps> = ({ restaurantId, imageUrl, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const actionRef = useRef<HTMLButtonElement>(null);
    const mounted = useMountedRef();
    const { busy, error } = useTask(coverTask);
    const [status, setStatus] = useState('');
    const coverUrl = safeStorageImageUrl(imageUrl);

    const openPicker = () => {
        if (coverTask.get().busy) return;
        inputRef.current?.click();
    };

    /** Le bouton cliqué peut disparaître (zone vide → photo) : on garde le focus dans la section. */
    const keepFocus = () => {
        if (mounted.current && !sectionRef.current?.contains(document.activeElement)) actionRef.current?.focus();
    };

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // permet de re-choisir le même fichier après une erreur
        if (!file || coverTask.get().busy) return;
        coverTask.set({ busy: 'upload', error: null });
        try {
            const image = await prepareImageForUpload(file, 1600);
            onChange(await firestoreService.setRestaurantCoverPhoto(restaurantId, image));
            if (mounted.current) setStatus('Photo de l\'établissement enregistrée.');
            coverTask.set({ busy: null });
        } catch (err) {
            coverTask.set({ busy: null, error: userMessage(err, "L'envoi de la photo a échoué. Réessayez.") });
        }
        keepFocus();
    };

    const handleRemove = async () => {
        if (coverTask.get().busy || !window.confirm('Retirer la photo de votre établissement ?')) return;
        coverTask.set({ busy: 'remove', error: null });
        try {
            await firestoreService.removeRestaurantCoverPhoto(restaurantId);
            onChange({ imageUrl: undefined, imageStoragePath: undefined });
            if (mounted.current) setStatus('Photo de l\'établissement retirée.');
            coverTask.set({ busy: null });
        } catch (err) {
            coverTask.set({ busy: null, error: userMessage(err, "La photo n'a pas pu être retirée. Réessayez.") });
        }
        keepFocus();
    };

    return (
        <section ref={sectionRef} className="bg-white p-6 sm:p-8 rounded-3xl border border-black/[0.08] space-y-4">
            <div>
                <h3 className="text-lg font-bold text-gray-800">Photo de l'établissement</h3>
                <p className="text-sm text-gray-500">
                    La photo principale de votre restaurant, bientôt visible par vos clients dans l'app atable.
                    Format paysage conseillé. Enregistrée dès l'envoi.
                </p>
            </div>

            <div className="relative h-56 rounded-2xl overflow-hidden bg-gray-50 border-2 border-dashed border-gray-200">
                {coverUrl ? (
                    <img src={coverUrl} alt="Photo de l'établissement" className="w-full h-full object-cover" />
                ) : (
                    <button
                        type="button"
                        onClick={openPicker}
                        aria-disabled={busy !== null}
                        className="w-full h-full flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <CameraIcon className="w-8 h-8 mb-2" />
                        <span className="text-sm font-medium">Ajouter une photo du lieu</span>
                    </button>
                )}
                {busy && (
                    <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center gap-2">
                        <SpinnerIcon className="w-8 h-8" />
                        <span className="text-sm font-medium text-gray-600">
                            {busy === 'upload' ? 'Envoi de la photo…' : 'Suppression…'}
                        </span>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button ref={actionRef} type="button" onClick={openPicker} aria-disabled={busy !== null} className={actionButton}>
                    {coverUrl ? 'Changer la photo' : 'Ajouter une photo'}
                </button>
                {coverUrl && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        aria-disabled={busy !== null}
                        className="px-4 py-2 text-red-600 font-bold rounded-lg hover:bg-red-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                    >
                        Retirer
                    </button>
                )}
                <span className="text-xs text-gray-400">JPG, PNG ou WebP — redimensionnée automatiquement.</span>
            </div>
            <input ref={inputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} className="hidden" tabIndex={-1} onChange={handleFile} />
            <ErrorBox message={error} />
            <StatusRegion text={status} />
        </section>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Galerie de plats
// ─────────────────────────────────────────────────────────────────────────────

interface DishPhotosEditorProps {
    restaurantId: number | string;
    photos: RestaurantPhoto[];
    onChange: (photos: RestaurantPhoto[]) => void;
}

type FocusTarget = { id: string; action: 'left' | 'right' | 'remove' } | { action: 'add' };

export const DishPhotosEditor: React.FC<DishPhotosEditorProps> = ({ restaurantId, photos, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const addRef = useRef<HTMLButtonElement>(null);
    const pendingFocus = useRef<FocusTarget | null>(null);
    const mounted = useMountedRef();
    const { busy, error } = useTask(dishTask);
    const [status, setStatus] = useState('');
    const remaining = Math.max(0, MAX_DISH_PHOTOS - photos.length);
    const isBusy = busy !== null;

    // Après un déplacement / une suppression, la tuile change de place ou disparaît :
    // on remet le focus sur la photo déplacée ou sur la suivante.
    useEffect(() => {
        const target = pendingFocus.current;
        if (!target) return;
        pendingFocus.current = null;
        if (target.action === 'add') {
            addRef.current?.focus();
            return;
        }
        listRef.current
            ?.querySelector<HTMLButtonElement>(`[data-photo-id="${target.id}"][data-action="${target.action}"]`)
            ?.focus();
    }, [photos]);

    const announce = (text: string) => { if (mounted.current) setStatus(text); };

    const openPicker = () => {
        if (dishTask.get().busy || remaining === 0) return;
        inputRef.current?.click();
    };

    const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected: File[] = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = '';
        if (selected.length === 0 || dishTask.get().busy) return;

        const files = selected.slice(0, remaining);
        const problems: string[] = [];
        if (selected.length > files.length) {
            problems.push(`${selected.length - files.length} photo(s) non ajoutée(s) : ${MAX_DISH_PHOTOS} photos maximum.`);
        }

        // Envoi un par un : la galerie se remplit au fur et à mesure et un échec n'arrête pas les autres.
        let added = 0;
        // Galerie vide : la zone « Ajoutez vos premières photos » va disparaître.
        if (photos.length === 0) pendingFocus.current = { action: 'add' };
        dishTask.set({ busy: { kind: 'add', done: 0, total: files.length }, error: null });
        for (let i = 0; i < files.length; i++) {
            dishTask.set({ busy: { kind: 'add', done: i, total: files.length } });
            try {
                const image = await prepareImageForUpload(files[i], 1200);
                onChange(await firestoreService.addRestaurantDishPhoto(restaurantId, image));
                added++;
            } catch (err) {
                problems.push(`« ${files[i].name} » : ${userMessage(err, "l'envoi a échoué.")}`);
            }
        }
        dishTask.set({ busy: null, error: problems.length ? problems.join(' ') : null });
        announce(`${added} photo(s) ajoutée(s).${problems.length ? ' Certaines photos n\'ont pas pu être ajoutées.' : ''}`);
    };

    const move = async (index: number, delta: -1 | 1) => {
        const target = index + delta;
        if (dishTask.get().busy || target < 0 || target >= photos.length) return;
        const previous = photos;
        const moved = photos[index];
        const next = [...photos];
        [next[index], next[target]] = [next[target], next[index]];
        dishTask.set({ busy: { kind: 'reorder' }, error: null });
        pendingFocus.current = { id: moved.id, action: delta < 0 ? 'left' : 'right' };
        onChange(next); // affichage immédiat, annulé si l'enregistrement échoue
        try {
            onChange(await firestoreService.reorderRestaurantDishPhotos(restaurantId, next.map((p) => p.id)));
            dishTask.set({ busy: null });
            announce(`Photo déplacée en position ${target + 1} sur ${next.length}.`);
        } catch (err) {
            pendingFocus.current = { id: moved.id, action: delta < 0 ? 'left' : 'right' };
            onChange(previous);
            dishTask.set({ busy: null, error: userMessage(err, "Le nouvel ordre n'a pas pu être enregistré. Réessayez.") });
        }
    };

    const remove = async (photo: RestaurantPhoto, index: number) => {
        if (dishTask.get().busy || !window.confirm('Supprimer cette photo ?')) return;
        dishTask.set({ busy: { kind: 'remove', id: photo.id }, error: null });
        try {
            const next = await firestoreService.removeRestaurantDishPhoto(restaurantId, photo.id);
            const neighbour = next[Math.min(index, next.length - 1)];
            pendingFocus.current = neighbour ? { id: neighbour.id, action: 'remove' } : { action: 'add' };
            onChange(next);
            dishTask.set({ busy: null });
            announce(`Photo supprimée. ${next.length} photo(s) dans la galerie.`);
        } catch (err) {
            dishTask.set({ busy: null, error: userMessage(err, "La photo n'a pas pu être supprimée. Réessayez.") });
        }
    };

    const tileButton = 'p-1.5 bg-white/90 rounded-lg shadow hover:bg-white aria-disabled:opacity-40 aria-disabled:cursor-not-allowed';

    return (
        <section className="bg-white p-6 sm:p-8 rounded-3xl border border-black/[0.08] space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-bold text-gray-800">Photos de vos plats</h3>
                    <p className="text-sm text-gray-500">
                        Vos plus beaux plats, bientôt visibles par vos clients dans l'app atable ({photos.length}/{MAX_DISH_PHOTOS}).
                        La première photo s'affiche en premier. Enregistrées dès l'envoi.
                    </p>
                </div>
                <button
                    ref={addRef}
                    type="button"
                    onClick={openPicker}
                    aria-disabled={isBusy || remaining === 0}
                    className={actionButton}
                >
                    + Ajouter des photos
                </button>
            </div>
            <input ref={inputRef} type="file" accept={ACCEPTED_IMAGE_TYPES} multiple className="hidden" tabIndex={-1} onChange={handleFiles} />

            {busy?.kind === 'add' && (
                <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
                    <SpinnerIcon className="w-4 h-4" />
                    Envoi de la photo {busy.done + 1} sur {busy.total}…
                </div>
            )}
            <ErrorBox message={error} />
            <StatusRegion text={status} />

            {photos.length === 0 ? (
                <button
                    type="button"
                    onClick={openPicker}
                    aria-disabled={isBusy}
                    className="w-full h-40 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <CameraIcon className="w-8 h-8 mb-2" />
                    <span className="text-sm font-medium">Ajoutez vos premières photos de plats</span>
                </button>
            ) : (
                <ul ref={listRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" aria-busy={isBusy}>
                    {photos.map((photo, index) => {
                        const url = safeStorageImageUrl(photo.url);
                        const removing = busy?.kind === 'remove' && busy.id === photo.id;
                        const position = `${index + 1} sur ${photos.length}`;
                        return (
                            <li key={photo.id} className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100">
                                {url ? (
                                    <img src={url} alt={`Photo de plat ${position}`} loading="lazy" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">Image indisponible</div>
                                )}
                                <span aria-hidden="true" className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs font-bold">{index + 1}</span>
                                {removing && (
                                    <div className="absolute inset-0 bg-white/90 flex items-center justify-center">
                                        <SpinnerIcon className="w-6 h-6" />
                                    </div>
                                )}
                                <div className="absolute bottom-2 inset-x-2 flex justify-between">
                                    <div className="flex gap-1">
                                        <button type="button" onClick={() => move(index, -1)}
                                            data-photo-id={photo.id} data-action="left"
                                            aria-disabled={isBusy || index === 0}
                                            aria-label={`Déplacer la photo ${position} vers la gauche`} className={`${tileButton} text-gray-700`}>
                                            <ArrowIcon direction="left" />
                                        </button>
                                        <button type="button" onClick={() => move(index, 1)}
                                            data-photo-id={photo.id} data-action="right"
                                            aria-disabled={isBusy || index === photos.length - 1}
                                            aria-label={`Déplacer la photo ${position} vers la droite`} className={`${tileButton} text-gray-700`}>
                                            <ArrowIcon direction="right" />
                                        </button>
                                    </div>
                                    <button type="button" onClick={() => remove(photo, index)}
                                        data-photo-id={photo.id} data-action="remove"
                                        aria-disabled={isBusy}
                                        aria-label={`Supprimer la photo ${position}`} className={`${tileButton} text-red-600`}>
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
